import { useState, type ReactNode } from 'react';
import {
  Badge,
  Button,
  InShellPanel,
  InShellPanelDescription,
  InShellPanelHeader,
  InShellPanelTitle,
  Input,
  Label,
} from '@staff-app/ui';
import {
  AlertTriangle,
  Clock,
  Download,
  ExternalLink,
  Plane,
  Ban,
  PackageCheck,
  Pencil,
  ShieldAlert,
  RefreshCw,
  Truck,
} from 'lucide-react';
import {
  INTL_DEVICE_LABELS,
  INTL_STAGE_LABELS,
  formatMinor,
  intlRequestNo,
  type IntlRequest,
} from '@staff-app/shared-types';
import { CopyButton, trackingClipboard } from '@/components/CopyButton';
import { carrierName, carrierTrackingUrl } from '../carrier-tracking';
import { IntlShipToEditor, canEditShipTo, shipToFrozenReason } from './IntlShipToEditor';
import { IntlInvoiceStatusBadge } from './IntlInvoiceStatusBadge';
import { IntlRootOverrideDialog, type RootOverride } from './IntlRootOverrideDialog';
import {
  countryName,
  useAddIntlRequestNote,
  useCanFile,
  useCanFulfil,
  useIntlMcfRawOrder,
  useIsRoot,
  useCancelIntlMcfOrder,
  useIntlRequestNotes,
  useRefreshIntlInvoice,
  useFulfilIntlViaChina,
  useRefreshIntlMcfOrder,
  useReleaseIntlMcfOrder,
  useSubmitIntlMcfOrder,
  useCancelIntlQuote,
  useCreateIntlQuote,
  useConvertIntlQuote,
  useRefreshIntlQuote,
} from '../hooks/useIntlRequests';
import { downloadIntlQuotePdf } from '../quote-pdf';
import { ConvertQuoteDialog } from './ConvertQuoteDialog';
import { SupersedeQuoteDialog } from './SupersedeQuoteDialog';

function fmt(d?: string | null): string {
  if (!d) return '—';
  const parsed = new Date(d);
  return isNaN(+parsed) ? '—' : parsed.toLocaleString();
}

/**
 * A WRITTEN-OFF invoice — Stripe's two ways of saying no money is coming.
 *
 * Mirrors the server's `WRITTEN_OFF_STATUSES`, which refuses both a submit and a release on
 * these (requester, 2026-09-17). Checked here so the buttons are not offered at all: a button
 * that exists only to produce a refusal teaches people the refusals are noise.
 *
 * ⚠️ NOT waivable by a root approval, unlike "not paid yet". An approval says the money will
 * come later; a void says it will not come at all, and the stamp stays on the row afterwards —
 * which is exactly how an approved request could have shipped against a voided invoice.
 */
const writtenOff = (r: IntlRequest) =>
  r.invoiceStatus === 'void' || r.invoiceStatus === 'uncollectible';

/**
 * Has AMAZON actually shipped it — released, with a tracking number on a package?
 *
 * ⚠️ NOT `stage === 'FULFILLED'`, and the difference is the point. A China shipment also puts the
 * request at FULFILLED, and a request can legitimately carry both (a split shipment, or a China
 * order arranged after an Amazon one). Reading the stage would hide Cancel on exactly the request
 * that most needs a held Amazon duplicate cancelled.
 *
 * The same reading `map` uses to derive FULFILLED on the Amazon side, and the same one the server
 * refuses a cancel on — so the badge, this button and the refusal cannot disagree.
 */
const amazonShipped = (r: IntlRequest) =>
  Boolean(r.mcfReleasedAt) && (r.mcfPackages ?? []).some((p) => p.trackingNumber);

/**
 * One labelled value — label ABOVE, value below, the shape `RmaDetailDrawer` settled on.
 *
 * NOT label-left / value-right, which is what this panel shipped with and what made it hard to
 * read: right-aligned values share no left edge, so the eye re-finds the start of every line, and
 * anything long enough to wrap (an address, a Stripe error) was squeezed into a narrow ragged
 * strip beside its label. Labels above give every value the same left edge and the full width.
 *
 * `wide` spans both columns, for anything that wraps.
 */
function Field({
  label,
  value,
  wide,
  mono,
  copyTestId,
  testId,
  children,
}: {
  label: string;
  value?: ReactNode;
  wide?: boolean;
  mono?: boolean;
  /** Present ⇒ this value gets a copy button. Absent ⇒ it does not. */
  copyTestId?: string;
  testId?: string;
  /** Rendered beneath the value, inside the same field — for an action that belongs to it. */
  children?: ReactNode;
}) {
  const empty = value === null || value === undefined || value === '';
  // Only a plain string can go on the clipboard. A rendered node (the highlighted deadline, a
  // link) has no single value to copy, and a button that put "[object Object]" there would be
  // worse than none — the same trap RmaDetailDrawer's `isCopyable` guards.
  const copyable = Boolean(copyTestId) && typeof value === 'string' && value.length > 0;

  return (
    <div className={wide ? 'sm:col-span-2' : undefined} data-testid={testId}>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd
        className={[
          'mt-0.5 whitespace-pre-wrap break-words text-sm',
          mono ? 'font-mono text-xs' : '',
          empty ? 'text-muted-foreground' : 'text-foreground',
        ].join(' ')}
      >
        {empty ? '—' : value}
        {copyable ? <CopyButton value={String(value)} label={label} testId={copyTestId} /> : null}
        {children}
      </dd>
    </div>
  );
}

/** A titled two-column grid. The rule above it is the separator — no boxes, no nesting. */
/**
 * Is there a quote still standing — one that exists and has not been cancelled?
 *
 * The JS twin of the server's `HAS_LIVE_QUOTE_SQL`, and the predicate that decides both the
 * derived stage and whether the direct-invoice route is open. A CANCELLED quote is not a quote:
 * the offer is withdrawn, so there is nothing left for a fresh invoice to contradict.
 */
function hasLiveQuote(r: IntlRequest): boolean {
  return Boolean(r.stripeQuoteId) && !quoteCancelled(r);
}

/**
 * Was the quote withdrawn?
 *
 * ⚠️ A CANCELLED QUOTE AFFORDS NOTHING. Not download — the document was withdrawn and handing a
 * customer a fresh copy of it is the opposite of what cancelling meant. Not convert, which the
 * server refuses. Not supersede, which needs something to supersede. Not cancel, which is done.
 * The section stays visible because the HISTORY is worth reading; every button goes.
 */
function quoteCancelled(r: IntlRequest): boolean {
  return r.quoteStatus === 'canceled';
}

/**
 * Has the quote lapsed? A quote with NO expiry never does.
 *
 * MIRRORS THE SERVER, which is the authority (`IS_QUOTE_EXPIRED_SQL` and its JS twin in `map()`).
 * A copy rather than a derived field because it is one comparison and the drawer must be able to
 * disable a button before any round trip — but it means the two can drift, so the server refuses
 * independently and this is only ever the earlier, friendlier half of the same refusal.
 */
function quoteExpired(r: IntlRequest): boolean {
  return Boolean(r.quoteExpiresAt) && new Date(r.quoteExpiresAt as string).getTime() < Date.now();
}

/**
 * Has the request changed since the quote was priced?
 *
 * The request stays editable while quoted — ship-to until the Amazon order is submitted, and the
 * quantity by correction — so a quote can outlive the facts behind it. The server stores what the
 * quote was priced FOR precisely so this is a comparison rather than a guess.
 */
function quoteStale(r: IntlRequest): boolean {
  return (
    (r.quoteQuantity != null && r.quoteQuantity !== r.quantity) ||
    (Boolean(r.quoteShipToCountry) && r.quoteShipToCountry !== r.shipToCountry)
  );
}

/** WHICH edit invalidated it — "was 7, is now 12" beats "this quote is stale". */
function staleReason(r: IntlRequest): string {
  const parts: string[] = [];
  if (r.quoteQuantity != null && r.quoteQuantity !== r.quantity) {
    parts.push(`quantity was ${r.quoteQuantity}, is now ${r.quantity}`);
  }
  if (r.quoteShipToCountry && r.quoteShipToCountry !== r.shipToCountry) {
    parts.push(`destination was ${r.quoteShipToCountry}, is now ${r.shipToCountry}`);
  }
  return parts.join('; ');
}

function Section({
  title,
  children,
  cols = true,
  action,
}: {
  title: string;
  children: ReactNode;
  cols?: boolean;
  /** Rendered on the heading row, for an action that belongs to this section alone. */
  action?: ReactNode;
}) {
  return (
    <section className="border-t pt-4">
      <div className="mb-3 flex min-h-6 items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-foreground">{title}</h3>
        {action}
      </div>
      {cols ? (
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</dl>
      ) : (
        <div>{children}</div>
      )}
    </section>
  );
}

/**
 * The detail panel for one international request.
 *
 * WHY A PANEL AND NOT A PAGE. The queue is where the work is decided — which request needs an
 * address, which invoice has not been paid — and a full-page detour loses that context and the
 * filters that got you there. `InShellPanel` sits below the global nav inside the shell, which is
 * the house primitive for exactly this (see `TestingDeviceDrawer`); `Sheet` would portal to the
 * body and cover the navigation. RMA's drawer hand-rolls its own `fixed inset-0` overlay, which
 * predates the primitive — its LAYOUT is what this follows, not its shell.
 *
 * IT SHOWS WHAT THE TABLE CANNOT. The row carries nine columns because that is what fits; this
 * carries the things a person opens a request to find — the full destination, the VAT number, the
 * HubSpot ids, who corrected it and when, what the invoice actually is, and the notes.
 *
 * READ-FIRST, with the two actions that were already reachable from the row (raise the invoice,
 * ask Stripe) and NOT the edit form. Correcting a request is a real need — IR-12's missing phone
 * has no home in the UI yet — but `updateIntlRequest` deserves its own considered surface rather
 * than being wedged in beside a read view; the server half is built and tested already.
 */
export function IntlRequestDetailDrawer({
  request,
  onClose,
  onRaiseInvoice,
}: {
  request: IntlRequest | null;
  onClose: () => void;
  onRaiseInvoice?: (r: IntlRequest) => void;
}) {
  const open = Boolean(request);
  const { data: notesData, loading: notesLoading } = useIntlRequestNotes(request?.id ?? null);
  const notes = notesData?.intlRequestNotes ?? [];

  /**
   * Which request is being address-edited — the ID, not a boolean, so clicking a different row
   * leaves edit mode instead of showing another request's form as if it were this one's.
   */
  const [editingId, setEditingId] = useState<number | null>(null);
  const editing = request !== null && editingId === request.id;

  const [noteText, setNoteText] = useState('');
  const [addNote, { loading: adding, error: noteError }] = useAddIntlRequestNote();
  const [refresh, { loading: refreshing }] = useRefreshIntlInvoice();

  // ── the QUOTE ──────────────────────────────────────────────────────────────────────────────
  const [convertQuote, { loading: converting, error: convertError }] = useConvertIntlQuote();
  /**
   * RAISING A QUOTE FROM THE DRAWER (requester, 2026-09-22).
   *
   * It was only ever on the intake card, in the block that appears straight after filing — so a
   * rep who filed a request and closed the card could come back and INVOICE it but not QUOTE it.
   * That put the safer of the two documents out of reach of the screen people actually return
   * to, which is the same asymmetry the 2026-09-21 pass fixed on the ROLE axis (support could
   * bill but not quote) and left standing on the SCREEN axis.
   *
   * Fires directly rather than opening a dialog, matching the card: a quote is what you send in
   * order to be GIVEN a PO, so asking for the number first would be asking for something that
   * does not exist yet. The PO is captured when the quote is CONVERTED.
   */
  const [createQuote, { loading: quoting, error: createQuoteError }] = useCreateIntlQuote();
  const [cancelQuote, { loading: cancellingQuote, error: cancelQuoteError }] =
    useCancelIntlQuote();
  const [refreshQuote, { loading: quoteRefreshing }] = useRefreshIntlQuote();
  /** Which request is being re-priced — the ID, matching how `editingId` scopes address edits. */
  const [supersedingId, setSupersedingId] = useState<number | null>(null);
  /** Convert opens a dialog now — the PO is captured there before anything is billed. */
  const [convertingId, setConvertingId] = useState<number | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const downloadQuote = async (id: number) => {
    setDownloadError(null);
    setDownloading(true);
    try {
      await downloadIntlQuotePdf(id);
    } catch (e) {
      // Shown inline rather than thrown: the server's message names the fix ("supersede it to
      // raise a fresh one"), and a rejected promise here would surface as nothing at all.
      setDownloadError((e as Error).message);
    } finally {
      setDownloading(false);
    }
  };
  const [submitMcf, { loading: submitting, error: submitError }] = useSubmitIntlMcfOrder();
  const [release, { loading: releasing, error: releaseError }] = useReleaseIntlMcfOrder();
  const [cancelMcf, { loading: cancelling, error: cancelError }] = useCancelIntlMcfOrder();
  const [refreshMcf, { loading: mcfRefreshing, error: mcfRefreshError }] = useRefreshIntlMcfOrder();
  /**
   * The CHINA route (requester, 2026-09-17) — the second way a request can ship.
   *
   * Inline form state rather than a dialog: the drawer is already a panel, and two fields do not
   * earn a second layer for somebody to dismiss.
   */
  const [fulfilChina, { loading: chinaSaving, error: chinaError }] = useFulfilIntlViaChina();
  const [chinaOpen, setChinaOpen] = useState(false);
  const [chinaNumber, setChinaNumber] = useState('');
  const [chinaCarrier, setChinaCarrier] = useState('');
  /**
   * Amazon's verbatim answer, fetched on demand.
   *
   * NOT loaded with the panel: it is a debugging window, it costs an Amazon round trip, and most
   * of the time nobody wants it. In production the server answers with its refusal instead of a
   * payload — the surface is non-prod only — so what renders there is the reason, which is the
   * honest thing to show for a button that cannot do anything.
   */
  const [loadRaw, { data: rawData, loading: rawLoading, error: rawError }] = useIntlMcfRawOrder();
  const canFulfil = useCanFulfil();
  const canFile = useCanFile();
  const isRoot = useIsRoot();
  /** Which root override is being confirmed, if any. */
  const [override, setOverride] = useState<RootOverride | null>(null);
  /**
   * Has Amazon actually shipped it? The two FULFILLED stages mean released AND tracked, which is
   * exactly when the shipment becomes the thing a reader wants first.
   */
  const fulfilled =
    request?.stage === 'FULFILLED' || request?.stage === 'FULFILLED_UNPAID';
  /**
   * Whatever the last fulfilment action said, if it refused.
   *
   * THIS EXISTS BECAUSE ITS ABSENCE WAS A BUG. Each button was `mutate(...).catch(() => {})`,
   * which swallowed the server's refusal and rendered nothing — so pressing Submit on an
   * environment with `MCF_ENABLED` unset looked like a dead button rather than like a feature
   * that is switched off and says so. The `catch` is still needed (an unhandled rejection would
   * reach the console), but the error has to be SHOWN, and these refusals are the most useful
   * text in the feature: they name the missing address field, the unregistered SKU, the
   * destination Amazon will not serve.
   *
   * One line rather than four: only one of these can be the last thing pressed.
   */
  const mcfActionError = submitError ?? releaseError ?? cancelError ?? mcfRefreshError;

  const submitNote = async () => {
    if (!request || noteText.trim().length < 3) return;
    try {
      await addNote({ variables: { requestId: request.id, text: noteText.trim() } });
      setNoteText('');
    } catch {
      // Rendered from the hook's `error`; the text is kept so nothing is retyped.
    }
  };

  // The address as ONE block rather than seven fields: it is one fact, and an operator reads it
  // the way it will be printed on a label. RmaDetailDrawer makes the same call for the same
  // reason. The country is spelled out — this panel is where somebody checks the destination.
  const address = request
    ? [
        request.shipToName,
        request.shipToLine1,
        request.shipToLine2,
        [request.shipToCity, request.shipToRegion, request.shipToPostalCode]
          .filter(Boolean)
          .join(', '),
        countryName(request.shipToCountry),
      ]
        .filter((l) => l && String(l).trim())
        .join('\n')
    : '';

  /**
   * The Amazon block, as a FUNCTION so it can be rendered in one of two positions.
   *
   * ONCE A SHIPMENT EXISTS IT MOVES TO THE TOP, above Customer (requester, 2026-09-10). The
   * order of this panel should follow what the reader came for, and that changes: while a
   * request is being set up the customer and the destination are the work, and the fulfilment
   * block is an empty promise at the bottom. Once Amazon has shipped it, the tracking number IS
   * the answer to every question anybody opens the request to ask — "where is it", "has it
   * gone", "what do I tell them" — and making somebody scroll past eight fields of billing
   * detail to reach it is the wrong way round.
   *
   * The threshold is the two FULFILLED stages: released AND a tracking number. Deliberately not
   * "an order exists" — a held order tells you nothing a courier could act on, so it stays in
   * its ordinary place.
   *
   * The section itself is Amazon MCF, direct — there is no Shopify order in this lane — and is
   * organised around the one question somebody opens it for: is this shipping, and if not, what
   * is it waiting for?
   */
  const chinaUrl = carrierTrackingUrl(request?.chinaCarrier, request?.chinaTrackingNumber);

  /**
   * MAY THIS BE SUBMITTED TO AMAZON — paid, or approved unpaid by root.
   *
   * Mirrors the server's payment gate, which reads `mcf_unpaid_approved_at` off the row since
   * approval stopped submitting on its own (requester, 2026-09-17). Without this the Submit
   * button stays hidden on exactly the requests root has just approved, and the approval would
   * look like it did nothing.
   */
  const maySubmit = (r: IntlRequest) =>
    !writtenOff(r) &&
    // ⚠️ ALREADY SHIPPED FROM CHINA — a hard refusal since 2026-09-17, not a warning. The server
    // refuses it too; this is what stops the button being offered at all.
    !r.chinaTrackingNumber &&
    (Boolean(r.invoicePaidAt) || r.invoiceStatus === 'paid' || Boolean(r.mcfUnpaidApprovedAt));

  /**
   * MAY THIS BE RECORDED AS SHIPPED FROM CHINA — has the invoice actually gone out?
   *
   * A LOWER BAR THAN AMAZON'S, on purpose. `maySubmit` demands PAID because it places an order;
   * this route records a shipment somebody already arranged, and shipping on an open invoice
   * ahead of payment is a normal decision here. The line is "the customer has been invoiced".
   *
   * THE SAME READING THE STAGE USES — `(invoiceStatus ?? 'draft') === 'draft'`, plus "no invoice
   * at all", which is earlier still. A request the tab shows as Invoice draft and one this hides
   * the button on must be the same set, or the panel argues with the badge above it.
   *
   * MIRRORS THE SERVER, and is not the gate. `fulfilViaChina` refuses independently — and it goes
   * one better, re-reading the invoice from Stripe before it refuses, because `invoiceStatus` is
   * a cache whose stale value is `draft`. That is why the copy below sends somebody to Refresh
   * rather than telling them it cannot be done: pressing it can genuinely change the answer.
   */
  const mayFulfilChina = (r: IntlRequest) =>
    Boolean(r.chinaTrackingNumber) ||
    (Boolean(r.stripeInvoiceId) && (r.invoiceStatus ?? 'draft') !== 'draft');

  const fulfilmentSection = (r: IntlRequest) => (
    <Section
      title="Fulfilment — Amazon"
      action={
        r.mcfOrderId ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refreshMcf({ variables: { requestId: r.id } }).catch(() => {})}
            disabled={mcfRefreshing}
            data-testid="intl-mcf-refresh"
            title="Ask Amazon for this order's status and tracking"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${mcfRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        ) : null
      }
    >
      {/* ── NO STANDING FULFILMENT SUGGESTION ANY MORE (requester, 2026-09-22) ─────────────
          A red note sat here on every request to Finland, Greece, Cyprus or Malta from
          2026-09-16: "Suggest using China Fulfilled for Finland. Amazon MCF is still available
          as an option." The four destinations where MCF freight was thought dear enough to be
          worth a second look.

          ⚠️ IT WAS A STANDING GUESS THAT THIS PANEL NOW ANSWERS WITH A NUMBER. Since 2026-09-22
          freight is Amazon's measured fee for the ACTUAL shipment, and the "What Amazon quoted"
          table above shows it per speed — so an operator can see whether THIS order to Helsinki
          is expensive rather than being told that orders to Helsinki generally are. A permanent
          warning beside a real figure is worse than none: it is advice that cannot be wrong, on
          a screen that now carries evidence that can.

          The China Fulfilled ACTION is untouched — `fulfilIntlViaChina` is destination-agnostic
          and still how a China shipment gets recorded. Only the advice went. */}

      {/* The server's words, verbatim. See `mcfActionError` — a swallowed refusal here
          reads as a broken button. */}
      {mcfActionError ? (
        <p
          className="flex items-start gap-1.5 text-sm text-destructive sm:col-span-2"
          data-testid="intl-mcf-action-error"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{mcfActionError.message}</span>
        </p>
      ) : null}

      {r.mcfOrderId ? (
        <>
          {/* OUR id, labelled as ours. It used to say "Amazon order", which read as
              though Amazon had issued it — it did not. An MCF fulfilment order has no
              Amazon order number at all: the seller names the order and Amazon
              addresses it by that name (verified against the live response,
              2026-09-09). Amazon's own id is `amazonShipmentId`, per shipment, and it
              exists only once something ships — shown under Tracking below. */}
          <Field
            label="Fulfilment order (ours)"
            value={r.mcfOrderId}
            mono
            copyTestId="intl-drawer-copy-mcf-id"
          />
          <Field
            label="Status"
            value={
              <span className="inline-flex flex-wrap items-center gap-1.5">
                {/* Amazon's own word, never re-worded — same rule as Stripe's. */}
                <span data-testid="intl-mcf-status">{r.mcfStatus ?? '—'}</span>
                {r.mcfHeld ? (
                  /* THE STATE THAT MATTERS. Held means Amazon has accepted the order
                     and is waiting for us — nothing is moving. It is styled as an
                     action-needed badge because that is what it is. */
                  <Badge variant={'outline' as never} className="text-warning" data-testid="intl-mcf-held">
                    {'Held — awaiting release'}
                  </Badge>
                ) : null}
                {r.mcfCancelledAt ? (
                  <Badge variant={'destructive' as never}>{'Cancelled'}</Badge>
                ) : null}
              </span>
            }
          />
          <Field label="Marketplace" value={r.mcfMarketplace} />
          <Field label="Seller SKU" value={r.mcfSellerSku} mono />
          <Field label="Shipping speed" value={r.mcfShippingSpeed} />
          {/* Split for the same reason as the release stamp below, and it matters more
              here than it looks: the submitter is usually `poller@optisigns.com` and
              the releaser a person, so two labelled fields make the machine half and
              the human half of this lane legible at a glance. */}
          <Field label="Submitted by" value={r.mcfSubmittedBy} />
          <Field
            label="Submitted at"
            value={fmt(r.mcfSubmittedAt)}
            testId="intl-mcf-submitted-at"
          />
          {/* SPLIT, matching "Raised by" / "Raised at" in the invoice block above —
              the release is the moment goods started moving, and its date is a fact
              people ask for on its own ("when did that ship?") rather than always in
              the same breath as who pressed the button. */}
          <Field label="Released by" value={r.mcfReleasedBy} />
          <Field
            label="Released to ship at"
            value={
              r.mcfReleasedAt ? (
                <span className="inline-flex items-center gap-1.5 font-medium text-success">
                  <Truck className="h-3.5 w-3.5 shrink-0" />
                  {fmt(r.mcfReleasedAt)}
                </span>
              ) : null
            }
            testId="intl-mcf-released-at"
          />
          <Field label="Last checked with Amazon" value={fmt(r.mcfStatusAt)} />

          {/* ── WHAT AMAZON QUOTED, EVERY SPEED (requester, 2026-09-17) ───────────────────────
              Captured at submit and kept, because a preview cannot be re-asked later: it prices
              a specific address, SKU, quantity and MOMENT.

              THE COLUMN THAT MATTERS IS THE LAST ONE — Amazon's fee against the freight we
              actually billed. That difference is the whole reason this is on screen ("then I can
              see do we need to increase the shipping cost later"), and it is per-order because
              MCF fees move with SKU, destination and quantity.

              ORDERED AS AMAZON RETURNED THEM, which is cheapest-first in practice. The speed we
              ordered is marked rather than sorted to the top: seeing Expedited sitting above it
              at +€34 is the comparison, and re-ordering would hide it. */}
          {r.mcfQuote?.length ? (
            <div className="sm:col-span-2" data-testid="intl-mcf-quote">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                {`What Amazon quoted${r.mcfQuotedAt ? ` — ${fmt(r.mcfQuotedAt)}` : ''}`}
              </p>
              <table className="mt-1 w-full text-xs">
                <thead className="text-muted-foreground">
                  <tr className="text-left">
                    <th className="font-normal">{'Speed'}</th>
                    <th className="font-normal">{'Amazon fee'}</th>
                    <th className="font-normal">{'Arrives'}</th>
                    <th className="font-normal">{'vs billed'}</th>
                  </tr>
                </thead>
                <tbody>
                  {r.mcfQuote.map((q) => {
                    const ordered = q.speed === r.mcfShippingSpeed;
                    const fee =
                      q.feeMinor == null
                        ? '—'
                        : formatMinor(q.feeMinor, (q.currency ?? r.currency ?? 'usd') as never);
                    // The margin line. Positive = the freight we billed covers Amazon's fee.
                    const billed = r.invoiceFreightMinor ?? null;
                    const margin =
                      q.feeMinor == null || billed == null ? null : billed - q.feeMinor;
                    return (
                      <tr
                        key={q.speed}
                        className={ordered ? 'font-medium text-foreground' : 'text-muted-foreground'}
                        data-testid={`intl-mcf-quote-${q.speed}`}
                      >
                        <td>
                          {q.speed}
                          {ordered ? (
                            <span className="ml-1.5 text-[10px] uppercase">{'ordered'}</span>
                          ) : null}
                          {!q.fulfillable ? (
                            <span className="ml-1.5 text-[10px] uppercase text-destructive">
                              {'not offered'}
                            </span>
                          ) : null}
                        </td>
                        <td className="font-mono">{fee}</td>
                        <td>
                          {q.earliestArrival
                            ? `${fmt(q.earliestArrival).split(',')[0]}${
                                q.latestArrival && q.latestArrival !== q.earliestArrival
                                  ? ` – ${fmt(q.latestArrival).split(',')[0]}`
                                  : ''
                              }`
                            : '—'}
                        </td>
                        <td
                          className={`font-mono ${
                            margin == null ? '' : margin < 0 ? 'text-destructive' : 'text-success'
                          }`}
                        >
                          {margin == null
                            ? '—'
                            : `${margin < 0 ? '-' : '+'}${formatMinor(
                                Math.abs(margin),
                                (q.currency ?? r.currency ?? 'usd') as never,
                              )}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {'“vs billed” compares Amazon’s estimate against the freight on this invoice. '}
                {'Negative means the shipment cost more than we charged for it.'}
              </p>
            </div>
          ) : null}
          {/* Only shown once it is above 1: on the common case it is noise, but "this
              is the third order id for this request" is something a reader needs. */}
          {r.mcfAttempt > 1 ? (
            <Field label="Attempt" value={String(r.mcfAttempt)} />
          ) : null}

          {/* TRACKING. Every package, because Amazon splits an order and 600 units is
              many boxes — a single "the tracking number" would be wrong for exactly
              the orders where it matters most. */}
          <div className="sm:col-span-2">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {'Tracking'}
            </p>
            {r.mcfPackages?.length ? (
              <ul className="mt-1 space-y-1" data-testid="intl-mcf-packages">
                {r.mcfPackages.map((p, i) => {
                  /**
                   * THE CARRIER'S OWN TRACKING PAGE, built from `carrierCode` (requester,
                   * 2026-09-17). The Shopify lane gets a `trackingUrl` handed to it; Amazon MCF
                   * gives us a carrier and a number and nothing else.
                   *
                   * Undefined for a carrier we do not recognise, and the number then renders as
                   * plain text — still copyable, still correct. A guessed URL gets pasted to a
                   * customer who sees "not found" and concludes their order never shipped.
                   */
                  // The marketplace picks the swiship HOST: a DE parcel tracks on swiship.de,
                  // and `.com` answers "not found" for it.
                  const url = carrierTrackingUrl(p.carrierCode, p.trackingNumber, r.mcfMarketplace);
                  return (
                  <li key={p.amazonShipmentId ?? p.trackingNumber ?? i} className="text-sm">
                    {p.trackingNumber && url ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-1 font-mono text-xs text-primary hover:underline"
                        data-testid={`intl-drawer-tracking-link-${i}`}
                      >
                        {p.trackingNumber}
                        <ExternalLink className="h-3 w-3 shrink-0" />
                      </a>
                    ) : (
                      <span className="font-mono text-xs">{p.trackingNumber ?? '—'}</span>
                    )}
                    {p.carrierCode ? (
                      <span className="ml-2 text-muted-foreground">
                        {carrierName(p.carrierCode) ?? p.carrierCode}
                      </span>
                    ) : null}
                    {p.trackingNumber ? (
                      /**
                       * COPIES THE LINK, NOT JUST THE NUMBER (requester, 2026-09-17).
                       * `trackingClipboard` puts both flavours on the clipboard — `text/html` as
                       * an anchor so a ticket reply or a doc receives a working link, and
                       * `text/plain` as "<number> <url>" for everything that takes only text.
                       *
                       * The same helper the Shopify lane uses, not a second one: this is exactly
                       * the case its docblock was written for.
                       */
                      <CopyButton
                        {...trackingClipboard(p.trackingNumber, url)}
                        label="Tracking number"
                        testId={`intl-drawer-copy-tracking-${i}`}
                      />
                    ) : null}
                    {/* THE AMAZON-MINTED ID — what to quote at Amazon, and the answer
                        to "what is the Amazon order number" for an MCF order. */}
                    {p.amazonShipmentId ? (
                      <div
                        className="text-xs text-muted-foreground"
                        data-testid={`intl-mcf-shipment-${i}`}
                      >
                        {'Amazon shipment '}
                        <span className="font-mono">{p.amazonShipmentId}</span>
                        {p.fulfillmentCenterId ? ` · from ${p.fulfillmentCenterId}` : ''}
                        {p.shipmentStatus ? ` · ${p.shipmentStatus}` : ''}
                        {/* Amazon's own estimate. Captured from the shipment since the
                            tracking work but never displayed — and it is the answer to
                            the question a customer actually asks after "has it
                            shipped": when does it get here. */}
                        {p.estimatedArrivalDate
                          ? ` · arrives ${fmt(p.estimatedArrivalDate)}`
                          : ''}
                        <CopyButton
                          value={p.amazonShipmentId}
                          label="Amazon shipment id"
                          testId={`intl-drawer-copy-shipment-${i}`}
                        />
                      </div>
                    ) : null}
                  </li>
                  );
                })}
              </ul>
            ) : (
              <p className="mt-1 text-sm text-muted-foreground">
                {r.mcfHeld
                  ? 'Nothing ships until the order is released.'
                  : 'No tracking from Amazon yet.'}
              </p>
            )}
          </div>

          {/* The override RECORD, when there is one. Above the raw response because
              it is about us rather than about Amazon. */}
          {r.mcfUnpaidApprovedAt ? (
            <Field
              label="Approved unpaid by"
              wide
              value={`${r.mcfUnpaidApprovedBy} · ${fmt(r.mcfUnpaidApprovedAt)} — ${r.mcfUnpaidReason}`}
              testId="intl-drawer-unpaid-record"
            />
          ) : null}
          {r.accountDeactivatedAt ? (
            <Field
              label="Account deactivated"
              wide
              value={
                <span className="text-destructive">
                  {`${r.accountDeactivatedBy} · ${fmt(r.accountDeactivatedAt)} — ` +
                    `${r.accountDeactivatedReason} (account ${r.deactivatedAccountId})`}
                </span>
              }
              testId="intl-drawer-deactivated-record"
            />
          ) : null}

          {/* ROOT ONLY: deactivate the customer's account over an unpaid shipment. Offered
              once the goods have actually gone and the invoice is still outstanding —
              the same two conditions the server enforces, so the button is not a
              route into a refusal. */}
          {isRoot &&
          r.mcfReleasedAt &&
          !r.accountDeactivatedAt &&
          !r.invoicePaidAt &&
          r.invoiceStatus !== 'paid' &&
          r.invoiceStatus !== 'void' ? (
            <div className="sm:col-span-2">
              <Button
                variant="outline"
                size="sm"
                className="border-destructive/50 text-destructive"
                onClick={() => setOverride('deactivate')}
                data-testid="intl-deactivate-open"
              >
                <Ban className="mr-1.5 h-3.5 w-3.5" />
                {'Deactivate the customer account'}
              </Button>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {'Shipped and still unpaid. Sets the account inactive — the same action '}
                {'the Devices Order chase uses.'}
              </p>
            </div>
          ) : null}

          {/* ── WHAT AMAZON ACTUALLY SAID ────────────────────────────────────────
              Verbatim, on demand. Worth a place on the panel because this lane's
              vendor has twice refused something for a reason no mapped field carried
              — a state name it wanted as `TX`, and a packaging feature its own
              `/features` endpoint claimed to support. Both were visible only in the
              raw exchange.

              NOTE THE CREATE ITSELF RETURNS AN EMPTY BODY, so this is the read-back
              rather than a record of the submission; there is nothing else to show. */}
          <div className="sm:col-span-2">
            {rawData || rawLoading || rawError ? (
              <>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  {'What Amazon says'}
                </p>
                {rawLoading ? (
                  <p className="mt-1 text-sm text-muted-foreground">{'Asking Amazon…'}</p>
                ) : (
                  <pre
                    className="mt-1 max-h-64 overflow-auto rounded-md border bg-card p-2 font-mono text-[11px] leading-relaxed"
                    data-testid="intl-mcf-raw"
                  >
                    {rawError ? rawError.message : rawData?.intlMcfRawOrder}
                  </pre>
                )}
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="px-0 text-muted-foreground"
                onClick={() =>
                  loadRaw({ variables: { requestId: r.id } }).catch(() => {})
                }
                data-testid="intl-mcf-raw-show"
              >
                {'Show Amazon’s response'}
              </Button>
            )}
          </div>

          {/* The two acts, in the section they act on. Hidden from anyone who cannot
              perform them rather than shown-and-refused. */}
          {canFulfil ? (
            <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
              {r.mcfHeld && writtenOff(r) ? (
                /* ⚠️ RELEASE IS THE IRREVERSIBLE ACT, and a written-off invoice must stop it.
                   Shown as a line rather than a disabled button: a disabled Release with no
                   explanation reads as a bug, and the actual next step is Cancel — which is
                   right beside it and still works. The poll cancels this automatically on its
                   next pass; this is the window before that. */
                <p
                  className="flex items-start gap-1.5 text-sm text-destructive sm:col-span-2"
                  data-testid="intl-mcf-written-off"
                >
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    {`The invoice is ${r.invoiceStatus}, so this cannot be released. Cancel the `}
                    {'order — the poll will do it anyway on its next pass.'}
                  </span>
                </p>
              ) : null}
              {r.mcfHeld && !writtenOff(r) ? (
                <Button
                  size="sm"
                  onClick={() => release({ variables: { requestId: r.id } }).catch(() => {})}
                  disabled={releasing}
                  data-testid="intl-mcf-release"
                >
                  <Truck className="mr-1.5 h-3.5 w-3.5" />
                  {releasing ? 'Releasing…' : 'Release to ship'}
                </Button>
              ) : null}
              {!r.mcfCancelledAt && !amazonShipped(r) ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => cancelMcf({ variables: { requestId: r.id } }).catch(() => {})}
                  disabled={cancelling}
                  data-testid="intl-mcf-cancel"
                >
                  {cancelling ? 'Cancelling…' : 'Cancel order'}
                </Button>
              ) : null}
              {!r.mcfCancelledAt && amazonShipped(r) ? (
                /* ⚠️ SHIPPED — CANCEL IS GONE (requester, 2026-09-18). Cancelling a fulfilment
                   order does not recall a parcel that is already with a carrier; all it would do
                   is stamp this request as cancelled while the goods are in transit, and whoever
                   reads the row next would be reading a lie.

                   A LINE RATHER THAN A DISABLED BUTTON, matching the written-off notice above: a
                   disabled control with no explanation reads as a bug, and the actual next step
                   is not on this panel at all. The tracking number is right above it. */
                <p
                  className="flex items-start gap-1.5 text-sm text-muted-foreground sm:col-span-2"
                  data-testid="intl-mcf-shipped-no-cancel"
                >
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    {'Amazon has shipped this — it cannot be cancelled here. Cancelling the '}
                    {'fulfilment order would not recall the parcel. Deal with it through the '}
                    {'carrier, or as a return once it arrives.'}
                  </span>
                </p>
              ) : null}
            </div>
          ) : null}
        </>
      ) : (
        <div className="sm:col-span-2">
          {/* NO ORDER YET. Three different situations, and they need different words:
              a failed attempt, a payment that has not arrived, and ready-to-go. */}
          {r.mcfError ? (
            <p
              className="flex items-start gap-1.5 text-sm text-destructive"
              data-testid="intl-mcf-error"
            >
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{r.mcfError}</span>
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              {r.invoicePaidAt || r.invoiceStatus === 'paid'
                ? 'Paid, and not yet submitted to Amazon.'
                : r.mcfUnpaidApprovedAt
                  ? 'Approved to ship before payment. Submit it, then release it — two presses, ' +
                    'because Amazon needs a moment between the two.'
                  : 'Nothing is submitted to Amazon until the invoice is paid.'}
            </p>
          )}
          {/* ⚠️ ALREADY SHIPPED FROM CHINA — REFUSED (requester, 2026-09-17).

              This was a warning beside a working button, which was the requester's earlier
              choice. Seeing it in practice changed the answer: a warning next to a live Submit
              is a second shipment waiting for one distracted afternoon, and the costs are
              asymmetric — a refused submit costs a conversation, a duplicate order costs a
              pallet.

              The escape hatch is named rather than hidden, because there IS a real case (the
              China shipment fell through) and an operator must be able to get out of it without
              guessing. */}
          {r.chinaTrackingNumber ? (
            <p
              className="mt-3 flex items-start gap-1.5 text-sm font-medium text-destructive"
              data-testid="intl-mcf-already-china"
            >
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                {'Already fulfilled from China, so this cannot be sent to Amazon — it would '}
                {'order the same goods twice. Clear the China tracking number first if that '}
                {'shipment did not happen.'}
              </span>
            </p>
          ) : null}
          {canFulfil && maySubmit(r) ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {/* ── HOW FAST — NOT A CHOICE ANY MORE (requester, 2026-09-22) ─────────────
                  "let the Expedited as default. we don't need to select Expedited or standard."

                  The dropdown here picked a speed at SUBMIT, after the invoice had been paid at
                  a price quoted for Standard — so choosing Expedited billed one speed and
                  shipped another. The old 50 + 20 tier hid that at seven to twelve times cost;
                  since freight became Amazon's own fee at a markup of 1.0, nothing does. Measured
                  2026-09-22: USD 7.57 Standard against USD 10.39 Expedited on a single US stick.

                  The service reads `MCF_SPEED_PREFERENCE` — Expedited, then Standard if Amazon
                  refuses it — and the PRICING reads the same list in the same order. What the
                  "What Amazon quoted" table above shows is still every speed, so the alternative
                  remains visible; it is just no longer something to pick wrong. */}
              <Button
                size="sm"
                onClick={() =>
                  submitMcf({ variables: { requestId: r.id } }).catch(() => {})
                }
                disabled={submitting}
                data-testid="intl-mcf-submit"
              >
                <PackageCheck className="mr-1.5 h-3.5 w-3.5" />
                {submitting ? 'Submitting…' : 'Submit to Amazon (held)'}
              </Button>
            </div>
          ) : null}
          {/* Says what pressing it does, because "held" is the whole safety property
              and an operator should not have to be told twice. */}
          {canFulfil && maySubmit(r) ? (
            <p className="mt-1.5 text-xs text-muted-foreground">
              {'Amazon accepts the order and reserves the stock. Nothing ships until '}
              {'somebody presses Release.'}
            </p>
          ) : null}

          {/* ROOT ONLY: approve shipping an OPEN invoice, in ONE press — it submits
              and releases. Offered only while there is an invoice that is genuinely
              awaiting payment: not on a draft (nothing has been asked for yet), not
              on paid (that is the normal path) and not on void or uncollectible
              (no money is coming, which is a different decision). */}
          {isRoot &&
          r.stripeInvoiceId &&
          !r.invoicePaidAt &&
          r.invoiceStatus !== 'paid' &&
          r.invoiceStatus !== 'draft' &&
          r.invoiceStatus !== 'void' &&
          r.invoiceStatus !== 'uncollectible' ? (
            <div className="mt-4 border-t pt-3">
              <Button
                variant="outline"
                size="sm"
                className="border-destructive/50 text-destructive"
                onClick={() => setOverride('shipUnpaid')}
                data-testid="intl-approve-unpaid-open"
              >
                <ShieldAlert className="mr-1.5 h-3.5 w-3.5" />
                {'Approve shipping before payment'}
              </Button>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {'Root only. It RECORDS the decision and unlocks Submit — it does not create '}
                {'the Amazon order and does not ship anything. Submit, then Release, as usual.'}
              </p>
            </div>
          ) : null}
        </div>
      )}

      {/* ── THE SECOND FULFILMENT ROUTE: SHIPPED FROM CHINA ─────────────────────────────────
          (requester, 2026-09-17.) Outside the `mcfOrderId` branch on purpose — a request can be
          fulfilled from China whether or not an Amazon order was ever created, and for the four
          remote destinations the whole point is that it never is.

          Recording it moves the request to FULFILLED and takes it off the Amazon lane, so the
          twice-daily poll cannot place a duplicate held order for goods already in transit. */}
      <div className="border-t pt-3 sm:col-span-2" data-testid="intl-china-block">
        {/* THE SAME WEIGHT AS A SECTION TITLE, not a field label (requester, 2026-09-17).
            `Section`'s own `h3` is `text-xs font-semibold uppercase tracking-wide text-foreground`
            and this is matched to it deliberately: China is a fulfilment ROUTE, peer to
            "Fulfilment — Amazon" above, and the muted label weight it shipped with read as a
            sub-heading of Amazon's section rather than an alternative to it. */}
        <h3 className="text-xs font-semibold uppercase tracking-wide text-foreground">
          {'Fulfilment — China'}
        </h3>

        {r.chinaTrackingNumber ? (
          <div className="mt-1.5 text-sm" data-testid="intl-china-recorded">
            {/* Same treatment as an Amazon package: a link when the carrier is one we can
                point at, plain text otherwise, and a copy that carries the hyperlink. */}
            {chinaUrl ? (
              <a
                href={chinaUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 font-mono text-xs text-primary hover:underline"
                data-testid="intl-china-tracking-link"
              >
                {r.chinaTrackingNumber}
                <ExternalLink className="h-3 w-3 shrink-0" />
              </a>
            ) : (
              <span className="font-mono text-xs">{r.chinaTrackingNumber}</span>
            )}
            {r.chinaCarrier ? (
              <span className="ml-2 text-muted-foreground">
                {carrierName(r.chinaCarrier) ?? r.chinaCarrier}
              </span>
            ) : null}
            <CopyButton
              {...trackingClipboard(r.chinaTrackingNumber, chinaUrl)}
              label="Tracking number"
              testId="intl-china-copy-tracking"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {`Recorded by ${r.chinaFulfilledBy ?? 'someone'} at ${fmt(r.chinaFulfilledAt)}.`}
            </p>
          </div>
        ) : null}

        {/* THE FORM, inline rather than a dialog: the drawer is already a panel, and two
            fields do not earn a second layer to dismiss. */}
        {canFulfil && mayFulfilChina(r) && chinaOpen ? (
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div>
              <Label className="text-xs text-muted-foreground">{'Tracking number *'}</Label>
              <Input
                className="mt-1"
                value={chinaNumber}
                onChange={(e) => setChinaNumber(e.target.value)}
                placeholder="e.g. 1Z999AA10123456784"
                data-testid="intl-china-number"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{'Carrier'}</Label>
              <Input
                className="mt-1"
                value={chinaCarrier}
                onChange={(e) => setChinaCarrier(e.target.value)}
                placeholder="DHL, UPS, FedEx…"
                data-testid="intl-china-carrier"
              />
            </div>
            <p className="text-xs text-muted-foreground sm:col-span-2">
              {'The carrier is optional — it only decides whether the number becomes a link. '}
              {'Recording this marks the request fulfilled and stops Amazon being asked to ship it.'}
            </p>
            <div className="flex items-center gap-2 sm:col-span-2">
              <Button
                size="sm"
                disabled={!chinaNumber.trim() || chinaSaving}
                onClick={() =>
                  fulfilChina({
                    variables: {
                      requestId: r.id,
                      input: {
                        trackingNumber: chinaNumber.trim(),
                        ...(chinaCarrier.trim() ? { carrier: chinaCarrier.trim() } : {}),
                      },
                    },
                  })
                    .then(() => {
                      setChinaOpen(false);
                      setChinaNumber('');
                      setChinaCarrier('');
                    })
                    .catch(() => {})
                }
                data-testid="intl-china-save"
              >
                {chinaSaving ? 'Saving…' : 'Save tracking number'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setChinaOpen(false)}
                data-testid="intl-china-cancel"
              >
                {'Cancel'}
              </Button>
            </div>
          </div>
        ) : canFulfil && mayFulfilChina(r) ? (
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => {
              // Pre-fill from what is already recorded, so a correction is an edit rather than
              // a retype — a mistyped tracking number is the likeliest thing to go wrong here.
              setChinaNumber(r.chinaTrackingNumber ?? '');
              setChinaCarrier(r.chinaCarrier ?? '');
              setChinaOpen(true);
            }}
            data-testid="intl-china-open"
          >
            <Plane className="mr-1.5 h-3.5 w-3.5" />
            {r.chinaTrackingNumber ? 'Edit the China tracking number' : 'China Fulfill'}
          </Button>
        ) : canFulfil ? (
          /*
            THE INVOICE HAS NOT GONE OUT (requester, 2026-09-18) — so there is nothing to record a
            shipment against, and the button is not offered.

            SAYS WHY, rather than being absent. An operator who came here to record a real parcel
            needs to know what to do next, and a missing button answers nothing. It names the
            remedy for both cases, and mentions Refresh because `invoiceStatus` is a cache whose
            stale value is `draft` — the invoice may well have finalized itself an hour after
            filing with nobody touching it.
          */
          <p
            className="mt-1.5 text-sm text-muted-foreground"
            data-testid="intl-china-blocked-draft"
          >
            {r.stripeInvoiceId
              ? 'The invoice is still a draft, so nothing can be recorded as shipped against ' +
                'it yet. It finalizes itself about an hour after filing — press Refresh above ' +
                'if that has already happened, or send it from Stripe.'
              : 'There is no invoice on this request yet, so nothing can be recorded as ' +
                'shipped against it. Raise the invoice first.'}
          </p>
        ) : r.chinaTrackingNumber ? null : (
          <p className="mt-1.5 text-sm text-muted-foreground">
            {'Not fulfilled from China.'}
          </p>
        )}

        {/* The server's words, verbatim — a swallowed refusal reads as a broken button, and
            this one refuses a malformed tracking number by name. */}
        {chinaError ? (
          <p
            className="mt-1.5 flex items-start gap-1.5 text-sm text-destructive"
            data-testid="intl-china-error"
          >
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{chinaError.message}</span>
          </p>
        ) : null}
      </div>
    </Section>
  );

  return (
    <InShellPanel
      open={open}
      onOpenChange={(next) => !next && onClose()}
      side="right"
      data-testid="intl-request-drawer"
      closeLabel="Close"
    >
      {/* Flex column so the header and the badge row stay put and the body scrolls — a request
          with a long Stripe error and a dozen notes is taller than the panel. */}
      <div className="flex h-full min-h-0 flex-col">
        {request ? (
          <>
            {/* `pr-8` keeps the title clear of the panel's own close button. */}
            <InShellPanelHeader className="shrink-0 pr-8">
              <InShellPanelTitle className="text-lg" data-testid="intl-drawer-title">
                <span className="font-mono">{intlRequestNo(request.id)}</span>
                {request.company ? (
                  <span className="text-muted-foreground">{` · ${request.company}`}</span>
                ) : null}
              </InShellPanelTitle>
              {/* The identity line: who this is for. The same address is a labelled field below,
                  but an operator reaching for it should not have to scan the grid — the call
                  RmaDetailDrawer's header makes, for the same reason. */}
              <InShellPanelDescription>
                {request.contactEmail}
                <CopyButton
                  value={request.contactEmail}
                  label="Contact email"
                  testId="intl-drawer-copy-email"
                />
              </InShellPanelDescription>
            </InShellPanelHeader>

            {/* State as BADGES rather than fields — four separate axes, read at a glance: how far
                along we are, what Stripe thinks of the money, what was ordered, and whether it
                can ship at all. A row of badges is also how RmaDetailDrawer opens. */}
            <div
              className="mb-4 flex shrink-0 flex-wrap items-center gap-2"
              data-testid="intl-drawer-badges"
            >
              {/* The same tones as the table's StageBadge — see its docblock for why
                  `Fulfilled unpaid` is the one stage styled as a problem. */}
              <Badge
                variant={
                  (request.stage === 'FULFILLED_UNPAID'
                    ? 'destructive'
                    : request.stage === 'FULFILLED'
                      ? 'default'
                      : request.stage === 'INVOICED' || request.stage === 'ORDER_CREATED'
                        ? 'secondary'
                        : 'outline') as never
                }
                data-testid={`intl-drawer-stage-${request.stage}`}
              >
                {INTL_STAGE_LABELS[request.stage] ?? request.stage}
              </Badge>
              {/* The SAME badge the table uses, so the queue and the panel cannot disagree
                  about what `open` looks like. */}
              {request.invoiceStatus ? (
                <IntlInvoiceStatusBadge status={request.invoiceStatus} prefix="Stripe:" />
              ) : null}
              <Badge variant={'outline' as never}>
                {`${request.quantity} × ${INTL_DEVICE_LABELS[request.deviceModel] ?? request.deviceModel}`}
              </Badge>
              {!request.addressComplete ? (
                <Badge variant={'destructive' as never} data-testid="intl-drawer-address-badge">
                  {'Address incomplete'}
                </Badge>
              ) : null}
              {/* The `Shipped unpaid` badge that used to sit here is GONE: the stage itself now
                  says `Fulfilled unpaid` (requester, 2026-09-10), and two badges making the same
                  claim invite the question of which one is authoritative. The APPROVAL — who
                  decided, and why — is still recorded as a field in the Fulfilment section,
                  because that is a different fact from the state. */}
              {request.accountDeactivatedAt ? (
                <Badge
                  variant={'destructive' as never}
                  data-testid="intl-drawer-deactivated-badge"
                  title={request.accountDeactivatedReason ?? undefined}
                >
                  {'Account deactivated'}
                </Badge>
              ) : null}
            </div>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
              {/* THE BLOCKER FIRST, when there is one. Somebody opening a request is usually
                  answering "what does this need from me", and an incomplete address or a failed
                  draft IS that answer — below six sections of reference data it would be buried. */}
              {!request.addressComplete || request.invoiceError ? (
                <div
                  className="space-y-1.5 rounded-md border border-destructive/40 bg-destructive/5 p-3"
                  data-testid="intl-drawer-blockers"
                >
                  {!request.addressComplete ? (
                    <p className="flex items-start gap-1.5 text-sm text-destructive">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>
                        {request.addressMissing?.length
                          ? `Needs the ${request.addressMissing.join(' and ')} before a courier can be booked.`
                          : 'The shipping address is incomplete.'}
                      </span>
                    </p>
                  ) : null}
                  {request.invoiceError ? (
                    <p className="flex items-start gap-1.5 text-sm text-destructive">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>{`The invoice was not drafted: ${request.invoiceError}`}</span>
                    </p>
                  ) : null}
                </div>
              ) : null}

              {/* FULFILMENT FIRST once it has shipped — see `fulfilmentSection`. */}
              {fulfilled ? fulfilmentSection(request) : null}

              <Section title="Customer">
                <Field label="Contact name" value={request.contactName} />
                <Field label="Company" value={request.company} />
                <Field
                  label="Stripe customer"
                  value={request.stripeCustomerId}
                  mono
                  copyTestId="intl-drawer-copy-customer"
                />
                <Field
                  label="VAT number"
                  value={request.vatNumber}
                  mono
                  copyTestId="intl-drawer-copy-vat"
                />
                <Field label="OptiSigns account" value={request.accountId} mono />
                <Field label="Invoice currency" value={(request.currency ?? 'usd').toUpperCase()} />
                <Field label="HubSpot contact" value={request.hubspotContactId} mono />
                <Field label="HubSpot deal" value={request.hubspotDealId} mono />
              </Section>

              {/* THE ONE EDITABLE THING (requester, 2026-09-09: "before the invoice is paid, we
                  still have the ability to update the shipping address. only the ship to info").
                  The Edit button is offered only while the window is open, and the reason is
                  shown when it is not — a disabled button would not say why. */}
              <Section
                title="Ship to"
                action={
                  canFile && canEditShipTo(request) && !editing ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingId(request.id)}
                      data-testid="intl-shipto-edit"
                    >
                      <Pencil className="mr-1.5 h-3.5 w-3.5" />
                      {'Edit'}
                    </Button>
                  ) : null
                }
              >
                {editing ? (
                  // `key` so switching rows mid-edit starts a fresh form rather than carrying
                  // one request's typing onto another.
                  <IntlShipToEditor
                    key={request.id}
                    request={request}
                    onDone={() => setEditingId(null)}
                  />
                ) : (
                  <>
                    <Field label="Address" value={address} wide testId="intl-drawer-shipto" />
                    <Field label="Phone" value={request.shipToPhone} />
                    <Field
                      label="Country"
                      value={`${countryName(request.shipToCountry)} (${request.shipToCountry})`}
                    />
                    {!canEditShipTo(request) ? (
                      <p
                        className="text-xs text-muted-foreground sm:col-span-2"
                        data-testid="intl-shipto-frozen"
                      >
                        {shipToFrozenReason(request)}
                      </p>
                    ) : null}
                  </>
                )}
              </Section>

              {/* ── THE QUOTE, ABOVE THE INVOICE ──────────────────────────────────────────
                  Order follows the lane: a quote comes before an invoice, and a request that
                  has both should read top-to-bottom as what happened in sequence.

                  RENDERED ONLY WHEN ONE EXISTS. Most requests never carry a quote — filing
                  straight to an invoice stays a complete journey — and an always-present empty
                  section would suggest a step somebody had skipped. */}
              {request.stripeQuoteId ? (
                <Section
                  title="Quote"
                  action={
                    /* REFRESHING IS RE-READING, so it sits on the read tier rather than with the
                       acts that change the quote. It was briefly root-only, which left a support
                       rep who had raised a quote unable to learn whether Stripe had moved it. */
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void refreshQuote({ variables: { requestId: request.id } })}
                      disabled={quoteRefreshing}
                      data-testid="intl-drawer-quote-refresh"
                    >
                      {quoteRefreshing ? 'Checking…' : 'Refresh'}
                    </Button>
                  }
                >
                  <Field
                    label="Quote number"
                    value={request.quoteNumber}
                    mono
                    copyTestId="intl-drawer-copy-quote-number"
                  />
                  <Field label="Status" value={request.quoteStatus} />
                  <Field
                    label="Total"
                    value={
                      request.quoteTotal != null
                        ? formatMinor(request.quoteTotal, request.quoteCurrency ?? 'usd')
                        : null
                    }
                  />
                  {/* ── WHAT IT COST US, beside what we charged ──────────────────────────────
                      Recorded at the moment the quote was raised (migration 053), because a
                      preview prices a MOMENT: IR-108 recorded EUR 8.56 on 2026-09-18 and the
                      identical lane quoted EUR 6.85 four days later. Re-deriving it now would
                      answer a different question from the one this quote was priced against.

                      ⚠️ TWO CURRENCIES, DELIBERATELY BOTH SPELLED OUT. Amazon answers in the
                      MARKETPLACE's currency and the quote is in the CUSTOMER's, and production
                      already holds a request where they differ (IR-109: USD quote, EUR cost).
                      A bare pair of numbers would read as a comparison that has not been made. */}
                  <Field
                    label="Freight — Amazon's cost"
                    testId="intl-drawer-quote-amazon-freight"
                    value={
                      request.quoteAmazonFeeMinor != null && request.quoteAmazonFeeCurrency
                        ? [
                            formatMinor(
                              request.quoteAmazonFeeMinor,
                              request.quoteAmazonFeeCurrency,
                            ),
                            request.quoteAmazonSpeed ?? '',
                            request.quoteShippingAmount != null
                              ? `· billed ${formatMinor(
                                  request.quoteShippingAmount,
                                  request.quoteCurrency ?? 'usd',
                                )}`
                              : '',
                          ]
                            .filter(Boolean)
                            .join(' ')
                        : null
                    }
                  />
                  <Field
                    label="Valid until"
                    value={
                      request.quoteExpiresAt ? (
                        <span
                          className={quoteExpired(request) ? 'font-medium text-destructive' : ''}
                          data-testid="intl-drawer-quote-expiry"
                        >
                          {new Date(request.quoteExpiresAt).toLocaleDateString()}
                          {quoteExpired(request) ? ' — expired' : ''}
                        </span>
                      ) : null
                    }
                  />
                  <Field label="Raised by" value={request.quoteCreatedBy} />
                  <Field label="Raised at" value={fmt(request.quoteCreatedAt)} />
                  {request.quoteRevision ? (
                    <Field
                      label="Revision"
                      value={`${request.quoteRevision} (superseded — see notes)`}
                    />
                  ) : null}

                  {/* ⚠️ THE STALENESS WARNING, and the reason the quote stores what it was
                      priced FOR. The request stays editable while quoted, so the numbers behind
                      a quote can move after the customer was shown it. Converting then bills a
                      price nobody agreed to — so the server refuses, and this says why BEFORE
                      somebody presses a disabled button and wonders. */}
                  {quoteStale(request) ? (
                    <p
                      className="flex items-start gap-1.5 text-sm font-medium text-destructive sm:col-span-2"
                      data-testid="intl-drawer-quote-stale"
                    >
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>
                        {`This quote no longer matches the request (${staleReason(request)}). `}
                        {'Supersede it to re-price before billing.'}
                      </span>
                    </p>
                  ) : null}

                  {/* EVERY ACTION GOES ONCE THE QUOTE IS CANCELLED — see `quoteCancelled`. The
                      section itself stays, because "we quoted them and withdrew it" is the part
                      of the history that reading the row later depends on. */}
                  {/* ── DOWNLOAD IS THE FILING TIER; EVERY OTHER ACTION HERE IS ROOT ────────
                      Support may hand a customer the quote (requester, 2026-09-21) — they can
                      already read every figure on it, so withholding the PDF that states them
                      protected nothing. OPERATING the quote — converting, superseding,
                      withdrawing — is a different act and stays root. */}
                  <div className="flex flex-wrap gap-2 sm:col-span-2">
                    {!canFile || quoteCancelled(request) ? null : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void downloadQuote(request.id)}
                        disabled={downloading || !request.quoteNumber}
                        data-testid="intl-drawer-quote-download"
                      >
                        <Download className="h-3.5 w-3.5" />
                        {downloading ? 'Preparing…' : 'Download the PDF'}
                      </Button>
                    )}
                    {/* CONVERT IS THE ONE THAT BILLS, so it is the only outlined-primary action
                        here, and it is disabled for a stale or expired quote rather than left to
                        fail server-side.

                        THE FILING TIER (root + support, 2026-09-21): the rep who raised the quote
                        closes it. It bills LESS freely than the Raise button they already have —
                        convert can only bill the figure the customer was shown, while a direct
                        raise takes a fresh one. */}
                    {canFile && !request.stripeInvoiceId && !quoteCancelled(request) ? (
                      <Button
                        size="sm"
                        /* Opens `ConvertQuoteDialog` rather than billing on the click. The PO is
                           captured there (requester, 2026-09-21), and an irreversible act that
                           emails a customer within the hour is the right place for the extra
                           half-second in which "wrong row" becomes visible. */
                        onClick={() => setConvertingId(request.id)}
                        disabled={converting || quoteStale(request) || quoteExpired(request)}
                        data-testid="intl-drawer-quote-convert"
                      >
                        {converting ? 'Converting…' : 'Convert to an invoice'}
                      </Button>
                    ) : null}
                    {/* ⚠️ RE-PRICING AND WITHDRAWING STAY ROOT, which is the one place a support
                        rep can still get stuck: Convert refuses a STALE or EXPIRED quote and the
                        fix is Supersede. The warning above names that, so the dead end is at
                        least legible rather than a greyed-out button with no explanation. */}
                    {isRoot && !request.stripeInvoiceId && !quoteCancelled(request) ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSupersedingId(request.id)}
                          data-testid="intl-drawer-quote-supersede"
                        >
                          {'Supersede'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            void cancelQuote({ variables: { requestId: request.id } })
                          }
                          disabled={cancellingQuote}
                          data-testid="intl-drawer-quote-cancel"
                        >
                          {cancellingQuote ? 'Cancelling…' : 'Cancel the quote'}
                        </Button>
                      </>
                    ) : null}
                  </div>

                  <p className="text-xs text-muted-foreground sm:col-span-2">
                    {quoteCancelled(request)
                      ? 'Withdrawn. Raise a new quote, or invoice the request directly.'
                      : request.stripeInvoiceId
                        ? 'Converted — the invoice below is what the customer pays.'
                        : 'Nothing is billed until this is converted.'}
                  </p>

                  {convertError || cancelQuoteError || downloadError || request.quoteError ? (
                    <p
                      className="text-sm text-destructive sm:col-span-2"
                      data-testid="intl-drawer-quote-error"
                    >
                      {convertError?.message ??
                        cancelQuoteError?.message ??
                        downloadError ??
                        request.quoteError}
                    </p>
                  ) : null}
                </Section>
              ) : null}

              <Section title="Invoice">
                <Field
                  label="Invoice number"
                  value={request.invoiceNumber}
                  mono
                  copyTestId="intl-drawer-copy-invoice-number"
                />
                <Field
                  label="Stripe invoice id"
                  value={request.stripeInvoiceId}
                  mono
                  copyTestId="intl-drawer-copy-invoice-id"
                />
                {/* THE CUSTOMER'S PURCHASE ORDER, captured at convert/raise time. Shown here
                    rather than only in Stripe because "which PO is this against" is asked of the
                    console, usually by somebody reconciling a payment weeks later. Absent on the
                    many orders placed without one — a PO is the buyer's instrument, not ours. */}
                {request.poNumber ? (
                  <Field
                    label="Purchase order"
                    value={request.poNumber}
                    mono
                    copyTestId="intl-drawer-copy-po-number"
                  />
                ) : null}
                {request.poDocumentId ? (
                  <Field
                    label="PO document"
                    value={
                      <a
                        href={`/staff/documents/${request.poDocumentId}`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-primary hover:underline"
                        data-testid="intl-drawer-po-doc"
                      >
                        {'Open in the Document Center'}
                      </a>
                    }
                  />
                ) : null}
                {/* Highlighted, and wide, because it is a DEADLINE: the draft finalizes and
                    emails the customer at this moment with nobody acting. */}
                <Field
                  label="Auto-sends at"
                  wide
                  value={
                    request.invoiceAutoSendAt ? (
                      <span
                        className="inline-flex items-center gap-1.5 rounded-sm bg-warning-bg px-1.5 py-0.5 font-medium text-warning"
                        data-testid="intl-drawer-autosend"
                      >
                        <Clock className="h-3.5 w-3.5 shrink-0" />
                        {fmt(request.invoiceAutoSendAt)}
                      </span>
                    ) : null
                  }
                />
                <Field label="Raised by" value={request.invoiceRaisedBy} />
                <Field label="Raised at" value={fmt(request.invoiceRaisedAt)} />
                <Field label="Paid at" value={fmt(request.invoicePaidAt)} />
                {/* "Checked, still unpaid" vs "nobody has looked since Tuesday" — and this is the
                    cache the stage is derived from, so its age matters. */}
                <Field label="Last checked with Stripe" value={fmt(request.invoiceCheckedAt)} />

                {/* The actions live in the section they act on, not as loose buttons at the foot
                    of the panel where they would read as actions on the whole request — the
                    placement RmaDetailDrawer argues for around its restocking-fee invoice. */}
                <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
                  {request.invoiceUrl ? (
                    <Button asChild variant="outline" size="sm">
                      <a
                        href={request.invoiceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="gap-1.5"
                        data-testid="intl-drawer-invoice-link"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        {'Open in Stripe'}
                      </a>
                    </Button>
                  ) : null}
                  {request.stripeInvoiceId ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        refresh({ variables: { requestId: request.id } }).catch(() => {})
                      }
                      disabled={refreshing}
                      data-testid="intl-drawer-refresh"
                    >
                      <RefreshCw
                        className={`mr-1.5 h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`}
                      />
                      {refreshing ? 'Asking Stripe…' : 'Refresh this invoice'}
                    </Button>
                  ) : /**
                     * ⚠️ NOT OFFERED WHILE A QUOTE STANDS. The mutual exclusion here used to be
                     * only "no invoice yet"; a live quote closes this route too.
                     *
                     * It is not about billing twice — nothing has been billed. It is the quieter
                     * failure: the customer is holding a document that says one figure, and this
                     * button prices from TODAY's catalog. Convert bills what was quoted; cancel
                     * the quote first to invoice a different figure. The server refuses this
                     * independently, so hiding the button is the explanation rather than the
                     * enforcement.
                     */
                  canFile && onRaiseInvoice && !hasLiveQuote(request) ? (
                    <>
                      {/* ── THE TWO DOCUMENTS, AS EQUALS ──────────────────────────────────────
                          Same pair, same order and the same styling as the intake card's decide
                          block: a quote and an invoice are alternatives, not a progression, and
                          styling either as primary would make the other read as the cautious
                          option rather than the different one.

                          Both carry the same condition as the Raise button already did — no
                          invoice, and no live quote. A quote standing closes BOTH routes: the
                          customer is holding a figure, and raising anything fresh prices from
                          today's catalog. Convert bills what was quoted; cancel to re-price. */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          createQuote({ variables: { input: { requestId: request.id } } }).catch(
                            () => {},
                          )
                        }
                        disabled={quoting}
                        data-testid="intl-drawer-create-quote"
                      >
                        {quoting ? 'Creating…' : 'Create a quote'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onRaiseInvoice(request)}
                        data-testid="intl-drawer-raise"
                      >
                        {'Raise the invoice'}
                      </Button>
                    </>
                  ) : null}
                </div>
                {/* ⚠️ THE REFUSAL IS SHOWN, NOT SWALLOWED, and it is load-bearing here rather
                    than defensive. Since freight became Amazon's measured fee there is no
                    fallback rate, so a request Amazon cannot price — stock out, half-typed
                    address, SP-API down, a currency the marketplace does not price in — is
                    refused by the server with a sentence naming which. Without this the button
                    would appear to do nothing. */}
                {createQuoteError ? (
                  <p
                    className="flex items-start gap-1.5 text-sm text-destructive sm:col-span-2"
                    data-testid="intl-drawer-create-quote-error"
                  >
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>{createQuoteError.message}</span>
                  </p>
                ) : null}
              </Section>

              {fulfilled ? null : fulfilmentSection(request)}

              <Section title="Filed">
                <Field label="Filed by" value={request.submittedBy} />
                <Field label="Filed at" value={fmt(request.submittedAt)} />
                <Field label="Source" value={request.source} />
                <Field
                  label="Last corrected"
                  value={
                    request.lastUpdatedBy
                      ? `${request.lastUpdatedBy} · ${fmt(request.lastUpdatedAt)}`
                      : null
                  }
                />
              </Section>

              <Section title="Notes" cols={false}>
                {notesLoading ? (
                  <p className="text-sm text-muted-foreground">{'Loading…'}</p>
                ) : notes.length ? (
                  <ul className="space-y-2" data-testid="intl-drawer-notes">
                    {notes.map((n) => (
                      <li key={n.id} className="rounded-md border bg-card p-2.5 shadow-sm">
                        <p className="whitespace-pre-wrap text-sm text-foreground">{n.text}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {`${n.staffEmail} · ${fmt(n.createdAt)}`}
                        </p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground" data-testid="intl-drawer-no-notes">
                    {'No notes yet.'}
                  </p>
                )}

                {/* WRITING a note is root (`addIntlRequestNote`); READING them is the wide
                    tier. Support and fulfilment see the history — which is most of the value,
                    since the notes carry why a price moved — and are not handed an input that
                    refuses on Enter. */}
                {isRoot ? (
                  <div className="mt-3 flex gap-2">
                    <Input
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Add a note…"
                      data-testid="intl-drawer-note-input"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') submitNote();
                      }}
                    />
                    <Button
                      variant="outline"
                      onClick={submitNote}
                      disabled={adding || noteText.trim().length < 3}
                      data-testid="intl-drawer-note-add"
                    >
                      {adding ? 'Adding…' : 'Add'}
                    </Button>
                  </div>
                ) : null}
                {noteError ? (
                  <p className="mt-1 text-xs text-destructive" data-testid="intl-drawer-note-error">
                    {noteError.message}
                  </p>
                ) : null}
              </Section>
            </div>

            {request && supersedingId === request.id ? (
        <SupersedeQuoteDialog
          request={request}
          open
          onOpenChange={(v) => setSupersedingId(v ? request.id : null)}
        />
      ) : null}

      {request && convertingId === request.id ? (
        <ConvertQuoteDialog
          request={request}
          open
          onOpenChange={(v) => setConvertingId(v ? request.id : null)}
        />
      ) : null}

      {override ? (
              <IntlRootOverrideDialog
                request={request}
                action={override}
                onClose={() => setOverride(null)}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </InShellPanel>
  );
}
